import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Award, Eye, Edit, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CommitteeScholarshipsTab = ({
  scholarships,
  isChairOrCoChair,
  openScholarshipDialog,
  archiveScholarship,
  unarchiveScholarship,
  deleteScholarship
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Committee Scholarships</h3>
        {isChairOrCoChair() && (
          <Button
            onClick={() => openScholarshipDialog()}
            size="sm"
            className="bg-greek-blue hover:bg-blue-700"
            data-testid="committee-create-scholarship-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Scholarship
          </Button>
        )}
      </div>
      
      {scholarships.length === 0 ? (
        <div className="p-8 text-center">
          <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No scholarships yet</p>
          {isChairOrCoChair() && (
            <p className="text-sm text-slate-500 mt-1">
              Create your first scholarship to get started
            </p>
          )}
        </div>
      ) : (
        <div className="divide-y divide-slate-200">
          {scholarships.map((scholarship) => {
            const statusClass = scholarship.is_archived
              ? 'bg-slate-200 text-slate-600'
              : scholarship.is_active
              ? 'bg-green-100 text-green-700'
              : 'bg-slate-100 text-slate-600';
            const statusLabel = scholarship.is_archived
              ? 'Archived'
              : scholarship.is_active
              ? 'Active'
              : 'Inactive';

            return (
              <div key={scholarship.id} className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900" data-testid={`committee-scholarship-name-${scholarship.id}`}>
                      {scholarship.name}
                    </h4>
                    <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-slate-500">
                      <span>${scholarship.amount?.toLocaleString()}</span>
                      <span>Deadline: {new Date(scholarship.deadline).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${statusClass}`}
                        data-testid={`committee-scholarship-status-${scholarship.id}`}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/preview/${scholarship.id}`)}
                      data-testid={`committee-scholarship-preview-btn-${scholarship.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    {isChairOrCoChair() && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openScholarshipDialog(scholarship)}
                          data-testid={`committee-scholarship-edit-btn-${scholarship.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {!scholarship.is_archived ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => archiveScholarship(scholarship)}
                            data-testid={`committee-scholarship-archive-btn-${scholarship.id}`}
                          >
                            <Archive className="w-4 h-4 mr-1" />
                            Archive
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => unarchiveScholarship(scholarship)}
                            data-testid={`committee-scholarship-unarchive-btn-${scholarship.id}`}
                          >
                            <ArchiveRestore className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteScholarship(scholarship)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`committee-scholarship-delete-btn-${scholarship.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommitteeScholarshipsTab;